import { host } from "../lib/url";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type MouseEvent } from "react";
import { EXTRACTOR, commentsLabel, mountReader, type Section } from "@vladrss/core";
import { bodyRequests } from "../lib/bodyRequests";
import { Icon } from "./Icon";
import { markSpeech, useListen } from "./Listen";
import { FeedIcon } from "./List";
import { LineLength, Seg, TextSize } from "./Controls";
import { Optional, Outline } from "./Outline";
import { useDiscussion } from "./Discussion";
import { api, useOwnerAction, useOwnerQuery } from "../lib/api";
import { useArticleActions, useArticleRow, useFeeds, type Feed } from "../lib/library";
import { setPrefs, usePrefs, type Prefs } from "../lib/prefs";
import { toggleNav } from "../lib/prefs";
import { sanitize } from "../lib/sanitize";
import { keep, keptAt } from "../lib/kept";
import { backToPlace, openSettings, rowCache, toast, update, useStore, withOverlay, type Row } from "../lib/store";

function useEnsure() {
  const ensure = useOwnerAction(api.content.ensure);
  return useCallback((id: string, row?: Row) => {
    if (row && (row.body === "full" || row.kind !== "article")) return Promise.resolve(null);
    return bodyRequests.run(`ensure:${id}`, () => ensure({ id: id as never }));
  }, [ensure]);
}

function useRefresh() {
  const refresh = useOwnerAction(api.content.refreshOutcome);
  return useCallback((id: string, body: { source: string; version?: number } | null | undefined) => {
    if (!body || body.source !== "extracted" || (body.version ?? 0) >= EXTRACTOR) return;
    void bodyRequests.run(`refresh:${id}:${EXTRACTOR}`, () => refresh({ id: id as never }));
  }, [refresh]);
}

/** A slider's divider reaching the middle or an edge: a light tick, where the device has one. */
const tick = () => { if (matchMedia("(pointer: coarse)").matches) navigator.vibrate?.(6); };

/** Whether the full text of this article is being fetched right now. */
function useFetching(id: string) {
  return useSyncExternalStore(bodyRequests.subscribe, () => bodyRequests.running(`ensure:${id}`));
}

/** Saving fills the bookmark with a small pop, so it registers without reading the toast. */
function pop(el: HTMLElement) {
  if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  el.querySelector("svg")?.animate([{ transform: "scale(.8)" }, { transform: "scale(1.1)" }, { transform: "scale(1)" }], { duration: 320, easing: "cubic-bezier(.3,1.3,.5,1)" });
}

export function Reader({ onNext, onPrev, onClose }: { onNext: () => void; onPrev: () => void; onClose: () => void }) {
  const reading = useStore((st) => st.reading)!;
  const focus = useStore((st) => st.focus);
  useStore((st) => st.overlay);
  const pendingRead = useStore((st) => st.pendingRead);
  const feeds = useFeeds();
  const { read, save } = useArticleActions();
  const [menu, setMenu] = useState(false);
  const [tocSlot, setTocSlot] = useState<HTMLSpanElement | null>(null); // where a narrow reader's Contents button goes
  // the open article's own row, live: full text arriving, read state from other devices
  const row = useArticleRow(reading.id);
  const r = row ? withOverlay(row) : undefined;
  const feed = feeds?.find((f) => f.id === r?.feedId);
  const i = reading.session.indexOf(reading.id);
  const n = reading.session.length;
  // what is still to read after this one (a row not yet seen counts as unread): the number that
  // goes down as you read, and agrees with the unread counts beside it
  const left = reading.session.slice(i + 1).filter((x) => { const o = rowCache.get(x); return !o || !withOverlay(o).isRead; }).length;
  const where = `Article ${i + 1} of ${n} · ${left ? `${left} unread after this` : "nothing unread after this"}`;
  // a list of read articles (Recently read, Saved) has none left unread: there the position says how far along
  const pos = (cls: string) => <span className={cls} aria-label={where} data-tip={`${i + 1} of ${n}`}>{left ? <><b>{left}</b> left</> : i < n - 1 ? <><b>{i + 1}</b> / {n}</> : "Last one"}</span>;

  if (!r) return <section className="rcol"><div className="empty"><p>{row === null ? "This article is no longer available." : "Loading…"}</p></div></section>;
  // about to be read counts as read; keeping it unread then just cancels the pending read
  const unread = !r.isRead && pendingRead !== r.id;
  const keepUnread = (e: MouseEvent<HTMLElement>) => {
    // where the button has no word beside it (a narrow reader, the Focus bar) and a touch has no
    // hover tip, the phone's brief "Kept unread" says what the hollow circle just became
    const wordless = !e.currentTarget.querySelector(".tl")?.getClientRects().length;
    if (!unread) { pop(e.currentTarget); if (wordless) toast("Kept unread", undefined, "circleDot"); } // the dot lands with the same small pop as a saved bookmark, in place: nothing beside it moves
    if (pendingRead === r.id) update({ pendingRead: null }); else read(r.id, unread);
  };
  const acts = (
    <>
      <button className={`ib tb${unread ? " on" : ""}`} onClick={keepUnread} aria-pressed={unread} aria-label="Keep unread" data-tip={unread ? "Kept unread · click to mark read" : "Keep this unread"} data-key="M">
        <Icon name={unread ? "circleDot" : "circle"} /><span className="tl swap"><span aria-hidden={unread}>Keep unread</span><span aria-hidden={!unread}>Kept unread</span></span>
      </button>
      <button className={`ib${r.isSaved ? " on" : ""}`} onClick={(e) => { if (!r.isSaved) pop(e.currentTarget); save(r.id, !r.isSaved); }} aria-pressed={r.isSaved} aria-label={r.isSaved ? "Saved" : "Save"} data-tip={r.isSaved ? "Saved" : "Save"} data-key="S"><Icon name={r.isSaved ? "bookmarkF" : "bookmark"} /></button>
      <a className="ib tb orig" href={r.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.currentTarget.blur()} aria-label="Open original" data-tip={`Open on ${host(r.url)}`} data-key="V"><Icon name="ext" /><span className="tl">Original</span></a>
    </>
  );
  return (
    <section className={`rcol${focus ? " focus" : ""}`} aria-label="Reader">
      <header className="rbar">
        <button className="ib navbtn" onClick={toggleNav} aria-label="Toggle sources" data-tip="Sources" data-key="["><Icon name="sidebar" /></button>
        <button className="ib listbtn" onClick={() => setPrefs({ listOff: false })} aria-label="Show the list" data-tip="Show the list" data-key={"\\"}><Icon name="listpane" /></button>
        <button className="btn ghost r-back" onClick={onClose} aria-label="Back to the list" data-key="Esc"><Icon name="arrowL" size={16} />Back</button>
        <span className="vsep" />
        <button className="ib" onClick={onPrev} disabled={i <= 0} aria-label="Previous article" data-tip="Previous" data-key="K"><Icon name="chevL" /></button>
        {pos("pos")}
        <button className="ib" onClick={onNext} disabled={i >= n - 1} aria-label="Next article" data-tip="Next" data-key="J"><Icon name="chevR" /></button>
        <span className="sp" />
        {/* wide: Focus stays on the bar; narrow: it lives in Aa */}
        <div className="vw">
          <button className={`ib tb${focus ? " on" : ""}`} onClick={() => update({ focus: !focus })} aria-pressed={focus} aria-label="Focus" data-tip="Focus: article only" data-key="F"><Icon name="maximize" /><span className="tl">Focus</span></button>
          <span className="vsep" />
        </div>
        {acts}
        <span className="toc-slot" ref={setTocSlot} />
        <span className="vsep" />
        <ReadingPopover />
        <div className="more-wrap">
          <button className="ib" onClick={() => setMenu(!menu)} aria-label="More" aria-haspopup="menu" aria-expanded={menu}><Icon name="more" /></button>
          {menu && (
            <div className="menu" role="menu" onMouseLeave={() => setMenu(false)}>
              <a className="mi orig-mi" role="menuitem" href={r.url} target="_blank" rel="noopener noreferrer" onClick={() => setMenu(false)}><Icon name="ext" size={16} />Open on {host(r.url)}</a>
              <button className="mi" role="menuitem" onClick={() => { navigator.clipboard?.writeText(r.url); setMenu(false); }}><Icon name="link" size={16} />Copy link</button>
              {feed && <button className="mi" role="menuitem" onClick={() => { setMenu(false); window.dispatchEvent(new CustomEvent("vladrss:go-feed", { detail: feed.id })); }}><FeedIcon feed={feed} />All from {feed.title}</button>}
            </div>
          )}
        </div>
      </header>
      <Single row={r} feed={feed} nextId={reading.session[i + 1]} tocSlot={tocSlot} />
      {[reading.session[i + 1], reading.session[i + 2], reading.session[i - 1]].filter((x): x is string => !!x).map((x) => <Prefetch key={x} id={x} />)}
      {focus && (
        <div className="fbar">
          <button className="ib" onClick={onPrev} disabled={i <= 0} aria-label="Previous" data-tip="Previous" data-key="K"><Icon name="chevL" /></button>
          {pos("fpos")}
          <button className="ib" onClick={onNext} disabled={i >= n - 1} aria-label="Next" data-tip="Next" data-key="J"><Icon name="chevR" /></button>
          <span className="vsep" />{acts}<span className="vsep" />
          <button className="ib" onClick={() => update({ focus: false })} aria-label="Leave focus" data-tip="Leave focus" data-key="Esc"><Icon name="x" /></button>
        </div>
      )}
    </section>
  );
}

/**
 * The reader's Aa button: everything about how the page reads in one place (focus,
 * text, theme, list pane), so the bar itself keeps only navigation and what to do with the
 * article. The same settings live in Reading & appearance, which the last row opens.
 */
function ReadingPopover() {
  const listen = useListen();
  const prefs = usePrefs();
  const focus = useStore((st) => st.focus);
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const set = (patch: Partial<Prefs>) => setPrefs(patch);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    // Esc closes the popover first; the reader's own Esc (back to the list) waits for the next press
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); setOpen(false); wrap.current?.querySelector<HTMLElement>(".aa")?.focus(); } };
    addEventListener("pointerdown", away);
    addEventListener("keydown", esc, true);
    return () => { removeEventListener("pointerdown", away); removeEventListener("keydown", esc, true); };
  }, [open]);
  return (
    <div className="more-wrap" ref={wrap}>
      <button className={`ib aa${open ? " open" : ""}`} onClick={() => setOpen(!open)} aria-haspopup="dialog" aria-expanded={open} aria-label="Reading settings" data-tip="Reading settings" data-key=",">
        <span className="aa-g" aria-hidden="true">Aa</span>
      </button>
      {open && (
        <div className="menu rpop" role="dialog" aria-label="Reading settings">
          <label className="pr pr-view"><span className="lab">Focus<small>Hide the list and sources</small></span>
            <input type="checkbox" role="switch" className="switch" checked={focus} onChange={(e) => update({ focus: e.target.checked })} />
          </label>
          <div className="pr-sep" />
          <div className="pr"><span className="lab">Text size</span><TextSize /></div>
          <div className="pr"><span className="lab">Typeface</span><Seg label="Typeface" value={prefs.font} options={[["serif", "Serif"], ["sans", "Sans"]]} onChange={(font) => set({ font })} /></div>
          <div className="pr pr-len"><span className="lab">Line length</span><LineLength /></div>
          <div className="pr"><span className="lab">Theme</span><Seg label="Theme" value={prefs.theme} options={[["system", "System", "monitor"], ["dark", "Dark", "moon"], ["light", "Light", "sun"]]} onChange={(theme) => set({ theme })} /></div>
          <div className="pr-sep" />
          <button className="mi" onClick={() => { setOpen(false); listen.start(); }}><Icon name="listen" size={16} />Listen to this article</button>
          <button className="mi" onClick={() => { setOpen(false); openSettings("reading"); }}><Icon name="sliders" size={16} />All reading settings</button>
        </div>
      )}
    </div>
  );
}


/**
 * Keeps the bodies of the articles around this one subscribed, with their photos
 * downloaded, so J and K show the next article on the next frame instead of loading it.
 */
export function Prefetch({ id, fetchFull = true }: { id: string; fetchFull?: boolean }) {
  const row = rowCache.get(id);
  const ensure = useEnsure();
  const body = useOwnerQuery(api.content.body, { id: id as never });
  const refresh = useRefresh();
  useEffect(() => { if (fetchFull) refresh(id, body); }, [id, body?.source, body?.version]); // eslint-disable-line react-hooks/exhaustive-deps
  // fetching a full page writes to the article, which re-reads its list page on the server: only for neighbours, not every hovered row
  useEffect(() => { if (row && fetchFull) void ensure(id, row); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  // The first photos warm the browser's cache once the page is idle: a light scan of the image
  // tags, not a full sanitize and parse (which cost tens of milliseconds as an article opened).
  useEffect(() => {
    const html = body?.html;
    if (!html) return;
    const warm = () => {
      for (const [tag] of [...html.matchAll(/<img\b[^>]*>/gi)].slice(0, 6)) {
        const attr = (n: string) => new RegExp(`\\s${n}\\s*=\\s*"([^"]*)"`, "i").exec(tag)?.[1]?.replace(/&amp;/g, "&");
        const src = attr("src");
        if (!src?.startsWith("https://")) continue;
        const pre = new Image();
        const sizes = attr("sizes"), srcset = attr("srcset");
        if (sizes) pre.sizes = sizes;
        if (srcset?.trim().startsWith("https://")) pre.srcset = srcset;
        pre.src = src;
      }
    };
    if ("requestIdleCallback" in window) { const h = requestIdleCallback(warm, { timeout: 2000 }); return () => cancelIdleCallback(h); }
    const t = setTimeout(warm, 400);
    return () => clearTimeout(t);
  }, [body?.html]);
  useEffect(() => { if (row?.imageUrl) new Image().src = row.imageUrl; }, [row?.imageUrl]);
  return null;
}

/** Where reading happens: this far below the top of the reader. */
const READING_LINE = 96;

/** What counts as a paragraph: the outermost reading blocks inside the article body. */
const READING_BLOCKS = "p, h1, h2, h3, h4, h5, h6, figure, blockquote, ul, ol, pre, table, iframe, img, video";
const paragraphs = (scroller: HTMLElement) =>
  [...scroller.querySelectorAll<HTMLElement>(`.prose :is(${READING_BLOCKS})`)].filter((b) => !b.parentElement?.closest(`.prose :is(${READING_BLOCKS})`));

/** The paragraph under the reading line, and how far into it: the same place whatever the window does meanwhile. */
function paragraphAt(scroller: HTMLElement): { block: number; within: number } | null {
  const line = scroller.getBoundingClientRect().top + READING_LINE;
  const blocks = paragraphs(scroller);
  for (let i = 0; i < blocks.length; i++) {
    const b = blocks[i]!.getBoundingClientRect();
    if (b.bottom > line) return { block: i, within: b.height ? Math.min(1, Math.max(0, (line - b.top) / b.height)) : 0 };
  }
  return null;
}

const NO_SECTIONS: Section[] = [];

function Single({ row, feed, nextId, tocSlot }: { row: Row; feed?: Feed; nextId?: string; tocSlot: HTMLElement | null }) {
  const scroller = useRef<HTMLDivElement>(null);
  const article = useRef<HTMLElement>(null);
  // the sections found in this article's body, kept with the article they belong to: the body
  // reports them before this component's own effects run, so nothing here resets them
  const [found, setFound] = useState<{ id: string; sections: Section[] } | null>(null);
  const sections = found?.id === row.id ? found.sections : NO_SECTIONS;
  const ensure = useEnsure();
  const next = nextId ? rowCache.get(nextId) : undefined;
  const disc = useDiscussion(row, scroller);

  // ask for the full text of this article and the next one
  useEffect(() => { void ensure(row.id, row); if (next) void ensure(next.id, next); }, [row.id, next?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Where reading is, noted as scrolling pauses, for this tab only (lib/kept.ts): a refresh lands back
  // there. Measured while scrolling, not on leaving: by then the reader already shows the next article.
  const measure = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const onScroll = () => {
    clearTimeout(measure.current);
    measure.current = setTimeout(() => {
      const el = scroller.current;
      if (!el) return;
      keep("reader", { top: el.scrollTop, id: row.id, ...(el.scrollTop < 40 ? {} : paragraphAt(el)) });
    }, 120);
  };

  // A new article starts at its top. After a refresh, the article the page opened on goes back to
  // where this tab had it, once its text is here, and never after reading has started.
  const [bodyFor, setBodyFor] = useState<string | null>(null);
  const onBodyReady = () => setBodyFor(row.id);
  useLayoutEffect(() => { if (scroller.current) scroller.current.scrollTop = 0; }, [row.id]);
  useLayoutEffect(() => {
    const el = scroller.current;
    const mine = keptAt("reader");
    if (!el || bodyFor !== row.id || backToPlace.id !== row.id || mine?.id !== row.id || !mine.top || el.scrollTop > 40) return;
    backToPlace.id = null;
    // the same paragraph, with the reading line where it was; without paragraphs, the same distance down
    const block = mine.block === undefined ? undefined : paragraphs(el)[mine.block];
    if (block) { const b = block.getBoundingClientRect(); el.scrollTop += b.top - el.getBoundingClientRect().top - READING_LINE + (mine.within ?? 0) * b.height; }
    else el.scrollTop = mine.top;
  }, [bodyFor, row.id]);

  return (
    <>
    <div className="rscroll" ref={scroller} tabIndex={-1} data-reader onScroll={onScroll}>
      <article className="reader" key={row.id} ref={article} data-article-id={row.id}>
        <ArticleHead row={row} feed={feed} disc={disc} />
        <ArticleBody row={row} feed={feed} onReady={onBodyReady} onSections={(s) => setFound({ id: row.id, sections: s })} />
        {/* the discussion is the article's last section, outside the text so nothing that reads the text sees it */}
        {disc.has && <section className="disc" ref={disc.hostRef} aria-label="Discussion" />}
        {/* an article simply ends; Space, J or the list move on. Only the list's end is said */}
        {!next && <footer className="r-end"><span className="end-note">End of the list</span></footer>}
      </article>
    </div>
    <Optional key={row.id}><Outline sections={sections} scroller={scroller} article={article} slot={tocSlot} after={disc.entry} /></Optional>
    </>
  );
}

/** An author that only repeats the source name ("Martin City Telegraph") is not shown. */
const simple = (x: string) => x.toLowerCase().replace(/^the\s+/, "").replace(/[^a-z0-9]/g, "");
const sameName = (a: string, b?: string) => !!b && simple(a) === simple(b);

function ArticleHead({ row, feed, disc }: { row: Row; feed?: Feed; disc?: { has: boolean; count?: number; jump: () => void } }) {
  const d = new Date(row.publishedAt);
  return (
    <header className="r-head">
      {/* a source with a discussion keeps room for its count from the start, so the count arriving moves nothing */}
      <div className={`r-meta${disc?.has ? " has-disc" : ""}`}>
        <FeedIcon feed={feed} size={18} /><span className="src">{feed?.title}</span>
        <span className="dot">·</span><time dateTime={d.toISOString()}>{d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", ...(d.getFullYear() !== new Date().getFullYear() ? { year: "numeric" } : {}) })}, {d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</time>
        {row.author && !sameName(row.author, feed?.title) && <><span className="dot">·</span><span className="author">{row.author}</span></>}
        {disc?.count ? (
          <button className="r-disc" onClick={disc.jump} aria-label={`${commentsLabel(disc.count)}: jump to the discussion`} data-tip="Jump to the discussion" data-key="C">
            <Icon name="comment" size={14} /><span>{disc.count.toLocaleString("en-US")}</span>
          </button>
        ) : null}
      </div>
      {/* the original opens in its own tab; coming back, the headline should not wear a focus ring */}
      <h1 className="headline"><a href={row.url} target="_blank" rel="noopener noreferrer" data-tip={`Open on ${host(row.url)}`} onClick={(e) => e.currentTarget.blur()}>{row.title}</a></h1>
    </header>
  );
}

function ArticleBody({ row, feed, onReady, onSections }: { row: Row; feed?: Feed; onReady?: () => void; onSections?: (s: Section[]) => void }) {
  const body = useOwnerQuery(api.content.body, { id: row.id as never });
  const fresh = useMemo(() => (body?.html ? sanitize(body.html, row.author) : ""), [body?.html, row.author]);
  const refresh = useRefresh();
  useEffect(() => { refresh(row.id, body); }, [row.id, body?.source, body?.version]); // eslint-disable-line react-hooks/exhaustive-deps
  const prose = useRef<HTMLDivElement | null>(null);
  // A better copy that arrives once reading is under way waits for the next visit: the text
  // never changes under the reader. Before then (the first moments after opening) it shows.
  const shown = useRef<{ id: string; html: string } | null>(null);
  let html = fresh;
  const s = shown.current;
  if (s && s.id === row.id && s.html && s.html !== fresh && prose.current) {
    const scroller = prose.current.closest("[data-reader]");
    if (scroller && prose.current.getBoundingClientRect().top < scroller.getBoundingClientRect().top - 40) html = s.html;
  }
  shown.current = { id: row.id, html };
  // the structure put back, comparisons as sliders, players as posters, photos full screen on a tap
  useLayoutEffect(() => {
    const el = prose.current;
    if (!el) return;
    delete el.dataset.enhanced; // the same element can hold a newer body (the full text after the excerpt)
    const mounted = mountReader(el, { title: row.title, tick, video: r => { if (r.state === "playing") dispatchEvent(new Event("vladrss:media-play")); } });
    markSpeech(el);
    // the sections of a long article, found once it is shaped (and again for a fuller copy)
    onSections?.(mounted.sections());
    return () => mounted.dispose();
  }, [html]); // eslint-disable-line react-hooks/exhaustive-deps
  const ready = useRef(false);
  const fetchingFull = useFetching(row.id);
  useEffect(() => { ready.current = false; }, [row.id]);
  useLayoutEffect(() => { if (body !== undefined && !ready.current) { ready.current = true; onReady?.(); } });

  if (row.kind === "comic") {
    const img = row.imageUrl;
    return <div className="prose"><figure className="comic">{img && <img src={img} alt={row.hoverText ?? row.title} referrerPolicy="no-referrer" />}{row.hoverText && <figcaption>{row.hoverText}</figcaption>}</figure></div>;
  }
  if (row.kind === "trail") {
    return (
      <div className="card trail">
        {row.imageUrl && <img src={row.imageUrl} alt="" referrerPolicy="no-referrer" />}
        <div className="card-b"><span className="caps">New trail</span><p>{row.preview || "A new trail was mapped. Trailforks sends a map tile, not an article."}</p><a className="btn primary" href={row.url} target="_blank" rel="noopener noreferrer">View on Trailforks<Icon name="ext" size={16} /></a></div>
      </div>
    );
  }
  if (body === undefined) return <div className="sk-body" aria-label="Loading"><span className="spin" />Loading…<i /><i /><i /><i /></div>;
  const partial = row.body !== "full";
  // the feed's photo leads an article without one of its own; a video's poster is its own
  const hasImg = /<(?:img|iframe)\b/i.test(html);
  return (
    <>
      {row.kind === "video" && <a className="card video" href={row.url} target="_blank" rel="noopener noreferrer">{row.imageUrl && <img src={row.imageUrl} alt="" referrerPolicy="no-referrer" />}<span><Icon name="play" />Plays on {host(row.url)}</span></a>}
      {!hasImg && row.imageUrl && row.kind === "article" && <figure className="hero"><img src={row.imageUrl} alt="" referrerPolicy="no-referrer" onError={(e) => e.currentTarget.parentElement?.remove()} /></figure>}
      {html ? <div className="prose" ref={prose} dangerouslySetInnerHTML={{ __html: html }} /> : <div className="prose"><p>{row.preview}</p></div>}
      {partial && fetchingFull && <div className="fetching" role="status" aria-label="Getting the full article"><i /><i /><i /></div>}
      {partial && !fetchingFull && (
        <div className="note excerpt"><Icon name="info" size={16} />
          <span>{feed?.serverBlocked ? <>Excerpt only here. {feed.title} blocks servers; the phone app fetches the full text.</> : <>Excerpt only. The full article couldn't be fetched.</>}</span>
          <a href={row.url} target="_blank" rel="noopener noreferrer">Read on {host(row.url)}</a>
        </div>
      )}
    </>
  );
}
